import { getAcpAppName } from "@libs/acp/utils/getAcpAppName";
import { buildHiddenContact } from "@libs/acp/utils/buildHiddenContact";
import { createContext, useEffect, useRef, useState } from "react";

import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { delay } from "@consumidor-positivo/ts-utils";

import { addCookie, removeCookie } from "@libs/utils/helpers/getSafeCookies";
import { legacyBase64Decrypt } from "@libs/utils/helpers/base64";
import { useRedline } from "@libs/redline";
import { useWebBridgeContext } from "@libs/app-bridge";
import { useUserStore } from "@libs/acp/store/user/useUserStore";
import type { UserACP } from "@libs/acp/store/user/types";
import { useFlowStore } from "@libs/acp/store/flow/useFlowStore";

import useFlowActionsHandler from "@libs/acp/flows/hooks/useFlowActionsHandler";

import {
	identityRefreshToken,
	identityEngageSignIn,
} from "@libs/acp/apis/signIn/signInAPI";
import {
	shouldRefreshTokens,
	checkSessionValidity,
	isJwt,
} from "@libs/acp/utils/handleToken";

import useContextWrapper from "./hook";
import { useTracking } from "@libs/acp/hooks/useTracking";

import { Flows } from "@libs/acp/flows/types";
import type {
	AuthenticationContextType,
	AuthenticationProviderProps,
} from "./types";

import constants from "@libs/acp/common/constants";

import {
	CHECKOUT_REDIRECT_LOCK_KEY,
	CheckoutRedirectService,
	useRkCheckoutFlowExperiment,
	clearRenegkitCache,
	useRKOffersListingExperiment,
} from "@libs/renegkit";
import { getHomePath } from "../../../../renegkit/src/experiments/RKOffersListing";

let authProviderRefreshInProgress: Promise<{ success: boolean }> | null = null;

export const AuthenticationContext = createContext<AuthenticationContextType>({
	isLoading: true,
	isAuthenticated: false,
	preventRedirect: false,
	tokens: {
		accessToken: "",
		refreshToken: "",
	},
});

export const useAuthentication = () =>
	useContextWrapper(AuthenticationContext, {
		name: "Authentication",
		provider: "AuthenticationProvider",
	});

const isCustomBuild = !!import.meta.env.VITE_CUSTOM_BUILD;

// A webview é remota e roda em QUALQUER versão do app. Apps antigos (anteriores ao módulo
// engagePushIdentity na bridge) não têm esses métodos nativos → a chamada falha no
// transporte. Blinda: método ausente/bridge indisponível vira no-op silencioso em vez de
// rejeição não tratada. (Independe da lógica de identidade da SDK, que roda só depois.)
const safeEngagePushCall = async (call: () => unknown): Promise<void> => {
	try {
		await call();
	} catch {
		// método não existe nesta versão do app: ignore
	}
};

export const AuthenticationProvider = ({
	children,
	addUser,
}: AuthenticationProviderProps) => {
	// State
	const [isLoading, setIsLoading] = useState(true);
	const [searchParams, setSearchParams] = useSearchParams();

	const hasCheckoutParam = searchParams.has("checkout");

	// Store
	const {
		authenticatedUser,
		updateACPAuthSession,
		addAuthenticatedUser,
		updateACPCustomer,
		resetACP,
	} = useUserStore();

	const {
		currentFlow: { activeFlows },
		addFlowResponse,
		updateFormState,
	} = useFlowStore();

	const unificationConflictinEmailPaths = {
		unificationValidateEmail: constants.conflictingEmailRedirects["email"],
		unificationUpdateEmail:
			constants.conflictingEmailRedirects["email_expired"],
		unificationUpdatePhone: constants.unificationPaths.UPDATE_PHONE,
		unificationUnifyPassword: constants.unificationPaths.UNIFY_PASSWORD,
	};

	const signInInfos = {
		ac: {
			redlineAppName: "acordo-certo",
			homePath: constants.pageNames.ac.home,
			...unificationConflictinEmailPaths,
		},
		cp: {
			redlineAppName: "consumidor-positivo",
			homePath: constants.pageNames.cp.home,
			...unificationConflictinEmailPaths,
		},
	};

	//TODO: essa função vai ser utilizada quando for instância de CP
	const tempEmptyOnSuccess = (_: unknown) => {};

	const { handleACPResponseToAPPs } = useFlowActionsHandler({
		onSuccess: addUser || tempEmptyOnSuccess,
		flowInfos: signInInfos,
	});

	const navigate = useNavigate();
	const location = useLocation();
	const { search } = location;
	const { getCorrelationId } = useRedline();
	const { bridge } = useWebBridgeContext();
	const lastSyncedPushIdentity = useRef<string | null>(null);

	const preventRedirect = activeFlows.includes(Flows.SIGNUP_UNIFIED);
	const { access_token, refresh_token } = authenticatedUser.acp.auth || {};
	const { customer } = authenticatedUser.acp;

	//APP
	const appName = getAcpAppName();

	// Hooks
	const {
		userAuthenticatedEvent,
		signInCompletedEvent,
		signInStartedEvent,
		signInErroredEvent,
	} = useTracking("sign-in");

	const handleRefreshToken = async (refreshToken: string) => {
		if (authProviderRefreshInProgress) {
			return authProviderRefreshInProgress;
		}

		authProviderRefreshInProgress = (async () => {
			try {
				const response = await identityRefreshToken(refreshToken);
				const refreshedTokens = response?.data?.data;

				updateACPAuthSession({
					...authenticatedUser.acp.auth,
					access_token: refreshedTokens?.access_token,
					refresh_token: refreshedTokens?.refresh_token,
					isAuthenticated: true,
				});

				return { success: true };
			} catch (error) {
				console.error("Error during token refresh:", error);
				return { success: false };
			} finally {
				authProviderRefreshInProgress = null;
			}
		})();

		return authProviderRefreshInProgress;
	};

	const backfillHiddenContact = () => {
		const { customer } = useUserStore.getState().authenticatedUser.acp;

		if (customer.hidden_email && customer.hidden_phone) return;

		const hiddenFallback = buildHiddenContact(
			customer.email ?? "",
			customer.phone ?? "",
		);

		const patch: Partial<UserACP["customer"]> = {};
		if (!customer.hidden_email && hiddenFallback.hidden_email) {
			patch.hidden_email = hiddenFallback.hidden_email;
		}
		if (!customer.hidden_phone && hiddenFallback.hidden_phone) {
			patch.hidden_phone = hiddenFallback.hidden_phone;
		}

		if (Object.keys(patch).length > 0) {
			updateACPCustomer(patch);
		}
	};

	const handleEngageLogin = async (token: string, isCached = false) => {
		const hasFlowParam = searchParams.get("flow");

		try {
			// Get url params to check utm for NCAD
			const utmParam = urlParams.get("utm") || undefined;

			const response = await identityEngageSignIn(
				token,
				String(isCached),
				utmParam,
				hasFlowParam,
			);

			if (!isCached) {
				removeEngageParamFromLocationSearchState();

				const userData: UserACP = response.data.data;
				const { customer } = userData;

				const hiddenFallback = buildHiddenContact(
					customer.email ?? "",
					customer.phone ?? "",
				);

				const authenticatedUserData: UserACP = {
					...userData,
					customer: {
						...customer,
						hidden_email:
							customer.hidden_email || hiddenFallback.hidden_email || "",
						hidden_phone:
							customer.hidden_phone || hiddenFallback.hidden_phone || "",
					},
				};

				addAuthenticatedUser(authenticatedUserData, "acp");
				const authType = "email-sign-in";
				const useProviderSuccess = true;

				handleACPResponseToAPPs({
					response: response.data,
					authType,
					useProviderSuccess,
					source: "signin_engage",
				});
			}

			dispatchAuthProviderLog("engage_login_success", { isCached });
			return { success: true };
		} catch (error) {
			console.error("Error during Engage login:", error);

			dispatchAuthProviderLog("engage_login_failed", {
				isCached,
				error: error instanceof Error ? error.message : String(error),
			});

			if (isCached) return { success: false };

			removeEngageParamFromLocationSearchState();

			if (axios.isAxiosError(error) && error.code !== "ERR_CANCELED") {
				const {
					error_slug,
					data: { document, hidden_email, hidden_phone, flow_slug },
					fields,
					message,
					error_code,
				} = error.response?.data || {};

				const signInErroredPayload = {
					error_code: error_code || "Unknown error code",
					error_slug: error_slug || "Unknown error slug",
					message: `Engage access error - ${message || "Unknown error message"}`,
				};

				const urlParams = new URLSearchParams(search);
				const utmParam = urlParams.get("utm") || undefined;

				if (utmParam && utmParam === "whatsappsignin" && document) {
					const signInErroredWpp = {
						...signInErroredPayload,
						message: "Engage access error - whatsapp sign-in redirect",
					};

					signInErroredEvent(signInErroredWpp);

					return {
						success: false,
						error: flow_slug,
						document: document,
					};
				}

				const isMFARequired = error_slug === Flows.MFA_REQUIRED;
				if (isMFARequired) {
					updateFormState({
						document,
						hidden_email,
						hidden_phone,
					});

					addFlowResponse([Flows.MFA_REQUIRED]);
				}

				const isInvalidDocumentField =
					document &&
					fields?.length &&
					fields?.[0]?.field_name === "document" &&
					fields?.[0]?.slug === "invalid_document";

				const shouldRedirectToNCADFlow =
					document?.length === 3 && isInvalidDocumentField;

				if (shouldRedirectToNCADFlow) {
					const redirectPath = constants.pageNames.signUpNCAD;

					const redirectLabel = hasCheckoutParam
						? "NCAD redirected"
						: "NCAD General redirected";

					const signInErroredNCAD = {
						...signInErroredPayload,
						message: `Engage access error - ${redirectLabel}`,
					};

					signInErroredEvent(signInErroredNCAD);

					navigate(`${redirectPath}${search}`, {
						state: { partialDocument: document },
					});

					return { success: false };
				}

				const isInvalidEmailField =
					hasFlowParam &&
					document &&
					fields?.length &&
					fields?.[0]?.field_name === "email" &&
					fields?.[0]?.slug === "invalid_email";

				const shouldRedirectToEmailConfirmation =
					hasFlowParam && document?.length === 3 && isInvalidEmailField;

				if (shouldRedirectToEmailConfirmation) {
					const signInErroredEmailConfirmation = {
						...signInErroredPayload,
						message: "Engage access error - Confirmação de e-mail",
					};

					signInErroredEvent(signInErroredEmailConfirmation);

					const navigationState = {
						partialDocument: document,
					};

					navigate(`${constants.pageNames.publicEmailConfirmation}${search}`, {
						state: navigationState,
					});

					return { success: false };
				}

				signInErroredEvent(signInErroredPayload);

				return { success: false };
			}

			return { success: false };
		}
	};

	const getNewTargetPathWithoutEngageParam = (path: string): string => {
		const [base, query] = path.split("?");

		if (!query) return base;

		const params = query.split("&").filter((param) => !param.startsWith("e="));
		return params.length > 0 ? `${base}?${params.join("&")}` : base;
	};

	const urlParams = new URLSearchParams(search);

	const removeEngageParamFromLocationSearchState = () => {
		const flowParam = urlParams.get("flow") || undefined;
		const utmParam = urlParams.get("utm") || undefined;
		const hasNCADUtm = utmParam && utmParam === "ncad";

		if (searchParams.get("e") && !hasNCADUtm && !flowParam) {
			searchParams.delete("e");
			setSearchParams(searchParams, { replace: true });
		}
	};

	const { isRenegkitVariant } = useRkCheckoutFlowExperiment();

	const redirectAuthenticatedUserFromPublicRoute = () => {
		const isInAPublicPath = constants.publicRoutes.includes(location.pathname);

		if (isInAPublicPath) {
			let defaultTargetPath = `${constants.pageNames[appName].home}${location.search}`;
			// dx-rk-offers-listing: AC variant users land on the new home after login.
      const acPath = getHomePath()
      const path = appName === "ac" ? acPath : defaultTargetPath

			let targetPath = `${path}${location.search}`;

			const checkoutRedirect = CheckoutRedirectService.getCheckoutRedirectUrl(
				undefined,
				isRenegkitVariant,
			);
			if (checkoutRedirect) {
				sessionStorage.setItem("temp_checkoutRedirect", checkoutRedirect);
				targetPath = checkoutRedirect;
			}

			setTimeout(() => {
				const urlParams = new URLSearchParams(location.search);
				const flowParam = urlParams.get("flow") || undefined;
				const utmParam = urlParams.get("utm") || undefined;
				const hasNCADUtm = utmParam && utmParam === "ncad";

				const shouldGetNewTargetPath =
					!checkoutRedirect &&
					searchParams.get("e") &&
					!hasNCADUtm &&
					!flowParam;

				if (shouldGetNewTargetPath) {
					targetPath = getNewTargetPathWithoutEngageParam(targetPath);
				}

				navigate(targetPath, {
					replace: true,
					state: {
						source: "authenticated_checkout_link",
						isFromCheckoutRedirect: !!checkoutRedirect,
					},
				});
			}, 0);
		}
	};

	const dispatchAuthProviderLog = (
		trigger: string,
		extra?: Record<string, unknown>,
	) => {
		window.dispatchEvent(
			new CustomEvent("logout_trail", {
				detail: {
					trigger,
					source: "acp_auth_provider",
					pathname: window.location.pathname,
					...extra,
				},
			}),
		);
	};

	const mapAtendeUserToACPCustomer = (
		user: Record<string, unknown>,
	): UserACP["customer"] =>
		({
			id: user["id"],
			uuid: user["uuid"],
			document: user["documento"],
			email: user["email"],
			birthdate: user["dataNascimento"],
			phone: user["celular"],
			full_name: user["nome"],
			social_name: (user["nomeSocial"] as string) ?? "",
			experience: "",
			customerIdHash: user["customerIdHash"],
			created_at: user["createdAt"],
		}) as UserACP["customer"];

	const addAtendeRlTracksAuthCookie = (atendeUser = "") => {
		try {
			const thirtyDays = new Date();
			thirtyDays.setDate(thirtyDays.getDate() + 30);
			addCookie({
				key: constants.redline.rl_tracks_auth,
				value: JSON.stringify({
					authenticationType: "atende-sign-in",
					isUnified: false,
					correlationId: getCorrelationId(constants.redline.RL_TRACK_CID),
					atendeUser,
				}),
				encrypt: true,
				expires: thirtyDays,
			});
		} catch (e) {
			console.warn("[Atende] Failed to set rl_tracks_auth cookie:", e);
		}
	};

	const handleAtendeSignIn = (atendeParam: string): boolean => {
		let atendeUser = "";
		try {
			const dataAtende = JSON.parse(legacyBase64Decrypt(atendeParam));
			atendeUser = (dataAtende.auth?.user as string) ?? "";
			const payload: UserACP = {
				auth: {
					access_token: dataAtende.auth.access_token,
					refresh_token: "",
					isAuthenticated: true,
				},
				customer: mapAtendeUserToACPCustomer(dataAtende.user),
			};
			addAuthenticatedUser(payload, "acp");
		} catch (error) {
			dispatchAuthProviderLog("atende_login_failed", {
				error: error instanceof Error ? error.message : String(error),
			});
			return false;
		}
		addAtendeRlTracksAuthCookie(atendeUser);
		return true;
	};

	// Recover user authentication state or perform any necessary setup
	useEffect(() => {
		(async () => {
			try {
				setIsLoading(true);
				await delay(500);

				const urlParams = new URLSearchParams(search);
				const atendeParam = urlParams.get("atende");
				if (atendeParam) {
					if (atendeParam === "signup") {
						addCookie({ key: "ATENDE_SIGNUP", value: "true" });
						if (useUserStore.getState().tokenOrigin() === "atende") {
							resetACP();
						}
					} else {
						const tokenBefore =
							useUserStore.getState().authenticatedUser.acp.auth.access_token;
						if (handleAtendeSignIn(atendeParam)) {
							removeCookie("TOKEN");
							removeCookie("SUBJECT");
							const tokenAfter =
								useUserStore.getState().authenticatedUser.acp.auth.access_token;
							if (tokenAfter !== tokenBefore) {
								localStorage.removeItem("SORTING_DEBTS_RES");
								clearRenegkitCache();
								const atendeCustomer =
									useUserStore.getState().authenticatedUser.acp.customer;
								userAuthenticatedEvent({
									authenticationType: "atende-sign-in",
									customerId: atendeCustomer.document,
									isUnified: Boolean(atendeCustomer.isUnified),
								});
							}
							return redirectAuthenticatedUserFromPublicRoute();
						}
					}
				}

				if (
					window === window.parent &&
					useUserStore.getState().tokenOrigin() === "atende"
				) {
					resetACP();
					removeCookie(constants.redline.rl_tracks_auth);
					removeCookie(constants.cookies.MFA_POSITIVE_DATA_PASSED);
				}

				//#region Validate initial session
				// Get the access and refresh tokens from the user store

				const { access_token, refresh_token } =
					useUserStore.getState().authenticatedUser.acp.auth;
				const { customer } = useUserStore.getState().authenticatedUser.acp;

				// Check if the initial session is valid
				const hasValidSession = checkSessionValidity(access_token);

				const firstAuthenticationType = sessionStorage.getItem(
					constants.acp.FIRST_AUTHENTICATION_TYPE,
				);
				const isAuthenticatedFirstWithEmail =
					firstAuthenticationType === "email-sign-in";

				// Get the URL parameters to check for an Engage token
				const urlAccessToken = urlParams.get("e");

				if (!hasValidSession || !!urlAccessToken) {
					dispatchAuthProviderLog("auth_check_started", {
						hasAccessToken: !!access_token,
						hasRefreshToken: !!refresh_token,
						hasCustomer: !!customer?.document,
						hasValidSession,
						hasUrlEngageToken: !!urlAccessToken,
						firstAuthenticationType,
					});
				}

				if (hasValidSession) {
					// Remove legacy AC cookies — user has valid ACP session
					removeCookie("TOKEN");
					removeCookie("SUBJECT");

					// Cached-sign-in: garante hidden_email/hidden_phone no customer persistido
					backfillHiddenContact();

					// Invalidate the URL token if it exists
					if (
						urlAccessToken &&
						isJwt(urlAccessToken) &&
						!isAuthenticatedFirstWithEmail
					) {
						// Fire and forget Engage login with URL token and passing isCached flag
						handleEngageLogin(urlAccessToken, true);
					}

					return redirectAuthenticatedUserFromPublicRoute();
				}

				//#region Refresh token
				// Check if we need to refresh tokens
				const shouldRefresh = shouldRefreshTokens(access_token, refresh_token);

				if (shouldRefresh) {
					dispatchAuthProviderLog("refresh_token_attempt", {
						hasAccessToken: !!access_token,
						hasRefreshToken: !!refresh_token,
					});

					const { success } = await handleRefreshToken(refresh_token);

					if (success) {
						// Remove legacy AC cookies — ACP refresh succeeded
						removeCookie("TOKEN");
						removeCookie("SUBJECT");

						// Invalidate the URL token if it exists
						if (urlAccessToken && isJwt(urlAccessToken)) {
							// Fire and forget Engage login with URL token and passing isCached flag
							handleEngageLogin(urlAccessToken, true);
						}

						// Cached-sign-in: garante hidden_email/hidden_phone no customer persistido
						backfillHiddenContact();

						userAuthenticatedEvent({
							authenticationType: "cached-sign-in",
							customerId: customer.document,
							isUnified: Boolean(customer.isUnified),
						});

						redirectAuthenticatedUserFromPublicRoute();
						return;
					}

					dispatchAuthProviderLog("refresh_token_failed", {
						hasAccessToken: !!access_token,
						hasRefreshToken: !!refresh_token,
					});

					throw new Error("Refresh token failed.");
				}

				//#region Engage signin (sem cached)
				// Check url search params token authentication
				const utmParam = urlParams.get("utm");
				const hasNCADUtm = utmParam && utmParam === "ncad";
				const isNCADPath = location.pathname === constants.pageNames.signUpNCAD;
				const isNCADPathAndFlow = isNCADPath && hasNCADUtm;

				if (urlAccessToken && isJwt(urlAccessToken) && !isNCADPathAndFlow) {
					signInStartedEvent();

					const { success, error, document } =
						await handleEngageLogin(urlAccessToken);

					if (success) {
						const freshCustomer =
							useUserStore.getState().authenticatedUser.acp.customer;

						userAuthenticatedEvent({
							authenticationType: "email-sign-in",
							customerId: freshCustomer.document,
							isUnified: Boolean(freshCustomer.isUnified),
						});

						signInCompletedEvent();

						sessionStorage.setItem(
							constants.acp.FIRST_AUTHENTICATION_TYPE,
							"email-sign-in",
						);

						return;
					}

					if (error && error === Flows.SIMPLE_SIGNIN) {
						const currentParams = new URLSearchParams(search);
						currentParams.delete("e");

						const newQuery = currentParams.toString();

						navigate(`${location.pathname}?${newQuery}`, {
							replace: true,
							state: {
								whatsappDocument: document,
							},
						});

						return;
					}
				}

				dispatchAuthProviderLog("reset_acp_no_session", {
					hasAccessToken: !!access_token,
					hasRefreshToken: !!refresh_token,
					hasValidSession,
					shouldRefresh,
					hasUrlEngageToken: !!urlAccessToken,
				});

				resetACP();
				removeCookie("user_logged_in");
				removeCookie(constants.cookies.MFA_POSITIVE_DATA_PASSED);
				sessionStorage.removeItem(CHECKOUT_REDIRECT_LOCK_KEY);
				sessionStorage.removeItem("ec-session-state");
			} catch (error) {
				console.error("Error during authentication check:", error);

				const acpAuth = useUserStore.getState().authenticatedUser?.acp?.auth;

				dispatchAuthProviderLog("reset_acp_catch_error", {
					error: error instanceof Error ? error.message : String(error),
					hasAccessToken: !!acpAuth?.access_token,
					hasRefreshToken: !!acpAuth?.refresh_token,
				});

				resetACP();
				removeCookie("user_logged_in");
				removeCookie(constants.cookies.MFA_POSITIVE_DATA_PASSED);

				if (isCustomBuild) {
					window.location.href = constants.pageNames.signin;
				} else {
					navigate(constants.pageNames.signin, { replace: true });
				}
			} finally {
				setIsLoading(false);
			}
		})();
	}, [location.pathname]);

	useEffect(() => {
		const firstAuthenticationType = sessionStorage.getItem(
			constants.acp.FIRST_AUTHENTICATION_TYPE,
		);
		const isAtendeSession = useUserStore.getState().tokenOrigin() === "atende";

		//TODO: temporário, vamos revisar a lógica do cached nas dashboards

		if (
			!isLoading &&
			Boolean(access_token) &&
			!firstAuthenticationType &&
			!isAtendeSession
		) {
			userAuthenticatedEvent({
				authenticationType: "cached-sign-in",
				customerId: customer.document,
				isUnified: Boolean(customer.isUnified),
			});
		} else {
			sessionStorage.removeItem(constants.acp.FIRST_AUTHENTICATION_TYPE);
		}
	}, [isLoading, access_token]);

	// Centraliza a sincronização da identidade do customer com a SDK de app-push do
	// Engage (via bridge da WebView): a SDK carimba os eventos device-state (token/
	// permission) quando logado e suprime quando deslogado. Fica aqui no AuthProvider
	// (acima das telas, compartilhado por AC, CP e shell) p/ não duplicar em cada app.
	// O ref deduplica: só repassa pra bridge quando a identidade muda.
	// Mandamos email + customerIdHash, mas a SDK DERIVA o customer_id do email
	// (lower(sha1(email)) = customer_id do Engage) e ignora o customerIdHash (que é hash
	// de analytics). O web não precisa conhecer a fórmula do Engage — só manda o email.
	useEffect(() => {
		if (!bridge.isWebViewBridgeAvailable || isLoading) return;

		const isAuthed = Boolean(access_token);

		// Logout: limpa uma única vez.
		if (!isAuthed) {
			if (lastSyncedPushIdentity.current === "logged-out") return;
			lastSyncedPushIdentity.current = "logged-out";
			void safeEngagePushCall(() => bridge.engagePushClearIdentity());
			return;
		}

		// Logado mas identidade ainda não populada: espera o próximo render.
		if (!customer?.customerIdHash && !customer?.email) return;

		// Logado (login ou já abriu logado): envia só quando a identidade muda.
		const next = JSON.stringify({
			customerIdHash: customer?.customerIdHash ?? null,
			email: customer?.email ?? null,
		});
		if (lastSyncedPushIdentity.current === next) return;
		lastSyncedPushIdentity.current = next;

		void safeEngagePushCall(() =>
			bridge.engagePushSetIdentity({
				customerIdHash: customer?.customerIdHash ?? null,
				email: customer?.email ?? null,
			}),
		);
	}, [isLoading, access_token, customer?.customerIdHash, customer?.email]);

	return (
		<AuthenticationContext.Provider
			value={{
				isLoading,
				isAuthenticated: !isLoading && Boolean(access_token),
				tokens: {
					accessToken: access_token,
					refreshToken: refresh_token,
				},
				customer,
				preventRedirect,
			}}
		>
			{children}
		</AuthenticationContext.Provider>
	);
};
